/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Offcanvas } from 'react-bootstrap';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import reportInvoiceService from 'service/reportInvoice';
import { useAppContext } from 'providers/AppProvider';
import Button from 'components/base/Button';
import ShowSerialNumber from './ShowSerialNumber';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';

export type PurchaseData = {
  product_name: string;
  barcode: string;
  category: string;
  unit: string;
  quantity: string;
  incoming_price: string;
  wholesale_price: string;
  retail_price: string;
  apply: string;
  total_price_received: string;
  total_price: string;
  serial_number: any;
};

const PurchaseByInvoiceTableResult = () => {
  const { t, i18n } = useTranslation();
  const {
    config: { theme }
  } = useAppContext();

  const { pathname, state } = useLocation();
  const invoiceId = pathname.split('/').pop();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const { name } = state;

  const [showSerialNumber, setShowSerialNumber] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const { data, isLoading, isFetching } = useQuery(
    ['GET_PRODUCTS', invoiceId, currentPage],
    () => {
      if (invoiceId) {
        const resData = reportInvoiceService
          .getReportProduct({
            limit: 10,
            offset: pageToOffset(currentPage, 10),
            invoice_id: invoiceId
          })
          .then((res: any) => {
            return res;
          });
        return resData;
      }
    },
    { enabled: !!invoiceId || !!currentPage }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  const productData: PurchaseData[] = useMemo(() => {
    const resultData =
      data?.products?.map((items: any) => {
        const total_price_received =
          items?.price_in && items?.quantity_in
            ? +items.price_in * +items.quantity_in
            : 0;
        const total_price =
          items?.price && items?.quantity_in
            ? +items.price * +items.quantity_in
            : 0;

        const measureUnitKindPid =
          data?.is_product === true
            ? items?.measure_unit_kind_pid
            : items?.nomenclature?.measure_unit_kind?.pid;

        const packageQty = items?.package_quantity ?? 1;
        const quantity = Math.floor((items?.quantity_in ?? 0) / packageQty);

        const remaining_package_quantity =
          (items?.quantity_in ?? 0) % packageQty;

        const quantityDisplay =
          measureUnitKindPid === 1
            ? `${quantity ?? 0}/${remaining_package_quantity ?? 0}`
            : items?.quantity_in ?? 0;

        return {
          product_name:
            data?.is_product === true ? items?.name : items?.nomenclature?.name,
          barcode: items?.barcode,
          unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          quantity: quantityDisplay,
          quantity_in: items?.quantity_in,
          package_quantity: items?.package_quantity,
          measure_unit_id: items?.nomenclature?.measure_unit_id,
          measure_unit_kind_pid: items?.nomenclature?.measure_unit_kind_pid,
          incoming_price: items?.price_in
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.price_in)
                .replace(/,/g, ' ')
            : 0,
          wholesale_price: items?.price_whosale
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.price_whosale)
                .replace(/,/g, ' ')
            : 0,
          retail_price: items?.price
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.price)
                .replace(/,/g, ' ')
            : 0,
          apply: items?.is_vat === true ? t('yes') : t('no'),
          /* prettier-ignore */
          total_price_received: total_price_received
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(total_price_received)
                .replace(/,/g, ' ')
            : 0,

          /* prettier-ignore */
          total_price: total_price
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(total_price)
                .replace(/,/g, ' ')
            : 0,

          serial_number: items?.partion_codes
        };
      }) ?? [];
    return resultData;
  }, [data, t, i18n?.language]);

  const purchaseDataTableColumns: ColumnDef<PurchaseData>[] = [
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'category',
      header: t('category'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'quantity',
      header: t('quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'incoming_price',
      header: t('price_admission'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'wholesale_price',
      header: t('wholesale_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'retail_price',
      header: t('price_difference'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'apply',
      header: t('apply_vat'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'total_price_received',
      header: t('total_price_admission'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'total_price',
      header: t('final_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'serial_number',
      header: t('serial_number'),
      cell: ({ row: { original } }) => {
        return (
          <div>
            {original.serial_number?.length > 0 && (
              <Button
                variant="soft-primary"
                onClick={() => {
                  setShowSerialNumber(true);
                  setSelectedRow(original);
                }}
              >
                {t('show')}
              </Button>
            )}
          </div>
        );
      },
      meta: {
        cellProps: {
          className: 'text-900',
          style: { whiteSpace: 'nowrap' }
        }
      }
    }
  ];

  const reprotsBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'reports',
      url: '/reports/purchase/invoice'
    },
    {
      label: 'purchase',
      url: '/reports/purchase/invoice'
    },

    {
      label: t('purchase_invoice'),
      url: '/reports/purchase/invoice'
    },
    {
      label: name || t('application_number'),
      active: true
    }
  ];
  const table = useAdvanceTable({
    data: productData,
    columns: purchaseDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div>
      <PageBreadcrumb items={reprotsBreadcrumbItems} />

      <div>
        <h2 className="mb-5">{name || t('application_number')}</h2>

        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
            <AdvanceTable
              tableProps={{ className: 'phoenix-table fs-9' }}
              isLoading={loading}
            />
            <AdvanceTableCPagination
              count={pageCount}
              page={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </AdvanceTableProvider>

        <div className={theme === 'dark' ? 'dark-footer-info' : 'footer-info'}>
          <div className="box">
            <p className="mb-0 text-bold">{t('total_price_admission')}</p>
            <span style={{ fontWeight: '800' }}>
              {data?.total_income_amount
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(data?.total_income_amount)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className="box">
            <p className="mb-0 text-bold">{t('total_sale_amount')}</p>
            <span style={{ fontWeight: '800' }}>
              {data?.total_sale_amount
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(data?.total_sale_amount)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className="box">
            <p className="mb-0 text-bold">{t('total_difference')}</p>
            <span style={{ fontWeight: '800' }}>
              {data?.total_sale_amount && data?.total_income_amount
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(data?.total_sale_amount - data?.total_income_amount)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>
        </div>
      </div>

      <Offcanvas
        show={showSerialNumber}
        onHide={() => {
          setShowSerialNumber(false);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('serial_number')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <ShowSerialNumber selectedRow={selectedRow} />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default PurchaseByInvoiceTableResult;
